// The activity clock: minutes per activity (activity_time, docs/PEDAGOGY.md §5.3), measured from
// use alone. One clock runs in the shell (useActivityClock in Layout). Time between two inputs
// (a tap, a key, a scroll) is credited to the activity that was current, each gap capped at
// IDLE_CAP_MS (the phone left on the table does not count); hidden time never counts. The
// activity is the one a screen claims (useActivity: a running deck claims its card's activity,
// an exercise round its own), else the place in the app (engine ROUTE_ACTIVITIES), else none.
// Stretches are written when the page is hidden or closed, and after every FLUSH_EVERY_S of
// active time; a stretch under MIN_SECONDS waits for more.
import { useEffect, useEffectEvent, useId } from 'react';
import { useLocation } from 'react-router';
import { routeActivity, type ActivityId } from '@valyrio/engine/pedagogy/strategies';
import { recordActivityTime } from './measure';

export const IDLE_CAP_MS = 90_000;
export const MIN_SECONDS = 5;
export const FLUSH_EVERY_S = 600;

export interface Stretch {
  activity: ActivityId;
  startedAt: string;
  seconds: number;
}

/** The clock's arithmetic, with time passed in (tested in app/test/measure.test.ts). */
export class ActivityClock {
  private last: number | null = null;
  private current: ActivityId | null = null;
  private acc = new Map<ActivityId, { ms: number; startedAt: number }>();

  constructor(private readonly cap = IDLE_CAP_MS) {}

  /** An input (or a change) at `now`: the gap since the last mark goes to the activity current during it. */
  mark(now: number): void {
    if (this.last !== null && this.current) {
      const gap = Math.min(Math.max(0, now - this.last), this.cap);
      if (gap > 0) {
        const a = this.acc.get(this.current);
        if (a) a.ms += gap;
        else this.acc.set(this.current, { ms: gap, startedAt: this.last });
      }
    }
    if (this.last !== null) this.last = now;
  }

  /** The activity from now on. */
  set(activity: ActivityId | null, now: number): void {
    this.mark(now);
    this.current = activity;
  }

  get activity(): ActivityId | null {
    return this.current;
  }

  /** Hidden: what was going on until now counts, the time away does not. */
  pause(now: number): void {
    this.mark(now);
    this.last = null;
  }

  /** Visible again (or the first input). */
  resume(now: number): void {
    if (this.last === null) this.last = now;
  }

  /** Active seconds not yet taken. */
  pending(): number {
    let ms = 0;
    for (const a of this.acc.values()) ms += a.ms;
    return ms / 1000;
  }

  /** The stretches of at least `min` seconds, taken out of the clock (shorter ones keep counting). */
  take(min = MIN_SECONDS): Stretch[] {
    const out: Stretch[] = [];
    for (const [activity, a] of this.acc) {
      const seconds = Math.round(a.ms / 1000);
      if (seconds < min) continue;
      out.push({ activity, startedAt: new Date(a.startedAt).toISOString(), seconds });
      this.acc.delete(activity);
    }
    return out;
  }
}

// ---------- the one clock ----------

const clock = new ActivityClock();
/** Activities claimed by mounted screens, newest last (the newest wins). */
const claims: { key: string; activity: ActivityId }[] = [];
let routeAct: ActivityId | null = null;

function refresh(now = Date.now()): void {
  const a = claims.at(-1)?.activity ?? routeAct;
  if (a !== clock.activity) clock.set(a, now);
}

function flush(min = MIN_SECONDS): void {
  const rows = clock.take(min);
  if (rows.length) recordActivityTime(rows);
}

/** The activity of the moment (recording scores are tagged with it). */
export function currentActivity(): ActivityId | null {
  return clock.activity;
}

/**
 * A screen doing one activity claims it while mounted (null claims nothing). The newest claim
 * wins over older ones and over the place in the app.
 */
export function useActivity(activity: ActivityId | null): void {
  const key = useId();
  useEffect(() => {
    if (!activity) return;
    claims.push({ key, activity });
    refresh();
    return () => {
      const i = claims.findIndex((c) => c.key === key);
      if (i >= 0) claims.splice(i, 1);
      refresh();
    };
  }, [key, activity]);
}

/** The shell's clock: inputs, visibility and the place in the app. Mount once (shell/Layout.tsx). */
export function useActivityClock(): void {
  const { pathname } = useLocation();
  const onPlace = useEffectEvent((p: string) => {
    routeAct = routeActivity(p);
    refresh();
  });
  useEffect(() => onPlace(pathname), [pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onInput = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') return;
      clock.resume(now);
      clock.mark(now);
      if (clock.pending() >= FLUSH_EVERY_S) flush();
    };
    const onVisibility = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') {
        clock.pause(now);
        flush();
      } else {
        clock.resume(now);
      }
    };
    const onHide = () => {
      clock.pause(Date.now());
      flush();
    };
    if (document.visibilityState !== 'hidden') clock.resume(Date.now());
    const opts = { capture: true, passive: true } as const;
    for (const ev of ['pointerdown', 'keydown', 'input', 'scroll'] as const) document.addEventListener(ev, onInput, opts);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    return () => {
      for (const ev of ['pointerdown', 'keydown', 'input', 'scroll'] as const) document.removeEventListener(ev, onInput, opts);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);
}
